import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  titulo: string;
  /**
   * El MISMO icono que la pantalla tiene en el sidebar (`__root.tsx:43`, la
   * lista `NAV`). Es lo que ancla el titulo: repetido del menu, el ojo lo lee
   * como "estas aca" y no como un adorno nuevo por pantalla.
   */
  icono?: LucideIcon;
  /**
   * Bajada. NO se usa en las pantallas del sistema: el subtitulo explicaba lo
   * que la pantalla ya muestra y se leia una sola vez, el primer dia. Queda
   * para el onboarding (`instalar.tsx`), que es el unico lugar donde el texto
   * es el contenido y no la decoracion del titulo.
   */
  descripcion?: string;
  acciones?: ReactNode;
  /** La linea de abajo. `false` fuera del shell (login, instalar). */
  linea?: boolean;
  className?: string;
}

/**
 * El encabezado de una ruta: el h1 de toda la app.
 *
 * Icono + linea, y no un subtitulo. Un titulo solo, en una pagina que arranca
 * con 24px de aire arriba, se lee como un texto suelto que quedo ahi: la
 * bajada en gris lo sostenia, pero al precio de un renglon de prosa que nadie
 * vuelve a leer despues del primer dia —y en las pantallas de alto fijo ese
 * renglon sale de la tabla—. El icono le da el peso que le daba el parrafo, y
 * la linea de abajo convierte el bloque en una franja de encabezado: separa lo
 * que es cromo de la pantalla de lo que son los datos.
 */
export function PageHeader({
  titulo,
  icono: Icono,
  descripcion,
  acciones,
  linea = true,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
        // `pb-3` contra el `gap-6` de `Pagina`: la linea queda mas cerca del
        // titulo que del contenido, asi que agrupa con el —que es lo que la
        // hace leer como el borde del encabezado y no como un separador
        // suelto en el medio de la pagina.
        linea && "border-b pb-3",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {Icono ? (
          // El mismo 36px de las pastillas de `BarraEstado` y de los botones
          // `sm:h-9` del header: es el alto con el que ya esta cuantizado todo
          // el cromo de arriba. El tile (fondo `accent`, icono a 20) es el
          // mismo de `EstadoVacio`, una medida mas chico.
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground [&_svg]:size-5"
          >
            <Icono />
          </span>
        ) : null}
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold tracking-tight">{titulo}</h1>
          {descripcion ? <p className="mt-1 text-sm text-muted-foreground">{descripcion}</p> : null}
        </div>
      </div>
      {acciones ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{acciones}</div>
      ) : null}
    </header>
  );
}
